import { Injectable } from '@angular/core';
import { CellData, TableData } from '@ddt-web/data-service';
import * as isNil from 'lodash/isNil';

@Injectable({
	providedIn: 'root'
})
export class TableSearchService {
	/**
	 * Searches the given search columns in the provided array (format of [key: string]: CellData).
	 * If the data array is null or undefined, the provided data array is returned.
	 *
	 * @param dataArrayToSort array to be searched in the format [key: string]: CellData
	 * @param searchColumns columns to search
	 * @param searchTerm term to search
	 * @returns the filtered array
	 */
	public searchDataArray(
		dataArrayToSearch: Array<{ [key: string]: CellData }>,
		searchColumns: string[],
		searchTerm: string,
		categoryRowColumns: string[] = []
	): Array<{ [key: string]: CellData }> {
		if (!searchTerm || isNil(dataArrayToSearch)) {
			return dataArrayToSearch;
		}

		const filteredData = dataArrayToSearch.filter((dataObject: { [key: string]: CellData }) => {
			if (categoryRowColumns.some(column => !!dataObject[column])) {
				return true;
			} else {
				return searchColumns.some(column =>
					dataObject[column]?.formattedValue?.toString()
						.toLowerCase()
						.includes(searchTerm.toLowerCase())
				);
			}
		});

		if (!categoryRowColumns.length || categoryRowColumns.length && filteredData.length > 1) {
			return filteredData;
		} else {
			return [];
		}
	}

	/**
	 * Searches the given search columns in the table data.
	 * If the table data is null or undefined or table data has no cells, the provided table data is returned.
	 *
	 * @param tableDataToSearch table data to be searched
	 * @param searchColumns columns to search
	 * @param searchTerm term to search
	 * @returns the filtered table data
	 */
	public searchTableData(tableDataToSearch: TableData, searchColumns: string[], searchTerm: string): TableData {
		if (!searchTerm || isNil(tableDataToSearch) || isNil(tableDataToSearch.cells)) {
			return tableDataToSearch;
		}

		const cellDataArray: CellData[][] = tableDataToSearch.cells.filter(row =>
			row.some(cell => {
				return searchColumns.some(
					column =>
						cell.config.id === column &&
						cell.formattedValue?.toString()
							.toLowerCase()
							.includes(searchTerm.toLowerCase())
				);
			})
		);

		return new TableData(
			tableDataToSearch.headersX,
			tableDataToSearch.headersY,
			cellDataArray,
			tableDataToSearch.portfolioData,
			tableDataToSearch.firstColumnTitle,
			tableDataToSearch.transposed
		);
	}
}
